import { useCallback, useMemo } from 'react';
import { getTableConfig } from './tableConfigs';
import useMuiTableBuilderV2, { StyledTableRow } from 'hooks/useMuiTableBuilderV2';
import styled from 'styled-components';
import { Box, TablePagination, TablePaginationProps } from '@mui/material';
import { useTeamMemberListItemsBuilder } from './helpers/useTeamMemberListItemsBuilder';
import { TeamMembershipType } from 'types';
import { CustomRowProps } from './types';
import { useSelector } from 'react-redux';
import { selectTeamMemberTableFilter } from 'selectors';
import useRequestSort from 'hooks/useRequestSort';
import { StyledEditIcon } from './DataCells/TeamMemberNameCell';
import { useGrowthBookFlag } from 'hooks/useGrowthBookFlag';

export const TeamMembersTable = ({
  selectTeamMember,
  selectedMemberIds,
  selectableMemberIds,
  selectAllSelectableMembers,
  clearAllSelectableMembers,
  teamMemberships,
  paginationProps,
  teamId,
}: {
  selectTeamMember: ({ accountId }: { accountId: number }) => void;
  selectedMemberIds: Set<number>; // accountIds
  selectableMemberIds: number[];
  selectAllSelectableMembers: () => void;
  clearAllSelectableMembers: () => void;
  teamMemberships: TeamMembershipType[];
  paginationProps: Pick<
    TablePaginationProps,
    'count' | 'page' | 'onPageChange' | 'rowsPerPage' | 'rowsPerPageOptions'
  >;
  teamId: number;
}) => {
  const listItemsBuilder = useTeamMemberListItemsBuilder({ teamId });
  const { isOn: showSignInStatus } = useGrowthBookFlag('memberManagementTableV2Flag');
  const tableConfig = useMemo(() => getTableConfig({ showSignInStatus }), [showSignInStatus]);

  const filter = useSelector(selectTeamMemberTableFilter);
  const { toggleSort } = useRequestSort({ filter });

  const mainList = useMemo(() => {
    return {
      listItems: listItemsBuilder({ teamMemberships }),
    };
  }, [listItemsBuilder, teamMemberships]);

  const onSort = useCallback<CustomRowProps['onSort']>(
    (columnId) => {
      toggleSort({ orderBy: columnId });
    },
    [toggleSort],
  );

  const _customRowProps: CustomRowProps = useMemo(
    () => ({
      selectTeamMember,
      selectedMemberIds,
      selectableMemberIds,
      selectAllSelectableMembers,
      clearAllSelectableMembers,
      orderDirection: filter.orderDirection,
      orderBy: filter.orderBy,
      onSort,
    }),
    [
      onSort,
      filter.orderBy,
      filter.orderDirection,
      selectTeamMember,
      selectedMemberIds,
      selectableMemberIds,
      selectAllSelectableMembers,
      clearAllSelectableMembers,
    ],
  );

  const renderFooterOptions = useCallback(() => {
    return <TablePagination component="div" color="primary" {...paginationProps} />;
  }, [paginationProps]);

  const { TableComponent } = useMuiTableBuilderV2({
    ...tableConfig,
    mainList,
    _customRowProps,
    tableMaxHeight: '1000px',
    renderFooterOptions,
  });

  return <StyledContainer>{TableComponent}</StyledContainer>;
};

const StyledContainer = styled(Box)`
  ${StyledEditIcon} {
    visibility: hidden;
  }

  ${StyledTableRow} {
    &:hover {
      ${StyledEditIcon} {
        visibility: visible;
      }
    }
  }
`;
